// src/components/common/Pagination.tsx
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
    Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui";

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    pageSizeOptions?: number[];
}

export function Pagination({
    currentPage,
    totalPages,
    pageSize,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [10, 20, 50, 100],
}: PaginationProps) {
    const { t } = useTranslation();

    return (
        <div className="flex items-center justify-between p-6">
            <div className="flex items-center gap-2">
                <p className="text-sm text-muted-foreground">
                    {t("common.rowsPerPage")}
                </p>
                <Select
                    value={pageSize.toString()}
                    onValueChange={(value) => onPageSizeChange(Number(value))}
                >
                    <SelectTrigger className="h-8 w-[70px]">
                        <SelectValue placeholder={pageSize.toString()} />
                    </SelectTrigger>
                    <SelectContent side="top">
                        {pageSizeOptions.map((size) => (
                            <SelectItem key={size} value={size.toString()}>
                                {size}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="flex items-center gap-2">
                <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                </Button>
                <div className="flex items-center gap-1">
                    <Button
                        variant={currentPage === 1 ? "default" : "outline"}
                        size="icon"
                        onClick={() => onPageChange(1)}
                    >
                        1
                    </Button>
                    {currentPage > 2 && <span>...</span>}
                    {currentPage > 1 && currentPage < totalPages && (
                        <Button variant="default" size="icon">
                            {currentPage}
                        </Button>
                    )}
                    {currentPage < totalPages - 1 && <span>...</span>}
                    {totalPages > 1 && (
                        <Button
                            variant={currentPage === totalPages ? "default" : "outline"}
                            size="icon"
                            onClick={() => onPageChange(totalPages)}
                        >
                            {totalPages}
                        </Button>
                    )}
                </div>
                <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                >
                    <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                </Button>
            </div>
        </div>
    );
} 